import { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Paper, Stack, Typography } from "@mui/material";

import { computeOrderTotals, formatMoney } from "../../../shared";
import { CustomDiscountField } from "../../../ui";

export const PriceSummarySection = () => {
  const { control, setValue } = useFormContext();

  const [
    subtotal,
    discountType,
    discountInput,
    taxable,
    totalIncludesTax,
    taxPercentage,
    salesInvoiceExpenses,
    salesDownPaymentAllocations,
  ] = useWatch({
    control,
    name: [
      "subtotal",
      "discountType",
      "discountInput",
      "taxable",
      "totalIncludesTax",
      "taxPercentage",
      "salesInvoiceExpenses",
      "salesDownPaymentAllocations",
    ],
  });

  // Memoize total expenses to prevent infinite loop
  const totalExpenses = useMemo(() => {
    return (salesInvoiceExpenses || []).reduce(
      (sum, expense) => sum + (expense.amount || 0),
      0
    );
  }, [salesInvoiceExpenses]);

  const totalDownPayment = useMemo(() => {
    return (salesDownPaymentAllocations || []).reduce(
      (sum, allocation) => sum + (Number(allocation.amount) || 0),
      0,
    );
  }, [salesDownPaymentAllocations]);

  // Totals are derived, never stored — see computeOrderTotals. Expenses are
  // folded in as additional charges so the grand total matches the backend.
  const { taxAmount, grandTotal } = useMemo(
    () =>
      computeOrderTotals({
        subtotal,
        discountType,
        discountInput,
        taxable,
        totalIncludesTax,
        taxPercentage,
        additionalCharges: totalExpenses,
      }),
    [
      subtotal,
      discountType,
      discountInput,
      taxable,
      totalIncludesTax,
      taxPercentage,
      totalExpenses,
    ]
  );

  const outstanding = Math.max(0, grandTotal - totalDownPayment);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack spacing={1}>
        {/* Subtotal Display */}
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">{formatMoney(subtotal)}</Typography>
        </Stack>

        {/* Discount Input */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Typography variant="body2">Diskon</Typography>
          <Controller
            control={control}
            name="discountInput"
            render={({ field, fieldState }) => (
              <CustomDiscountField
                value={field.value ?? 0}
                onChange={field.onChange}
                required
                fullWidth={false}
                baseAmount={subtotal ?? 0}
                discountType={discountType}
                onTypeChanged={(newType) => setValue("discountType", newType)}
                helperText={fieldState.error?.message}
                error={fieldState.invalid}
                inputProps={{
                  style: { textAlign: "right" },
                }}
              />
            )}
          />
        </Stack>

        {/* Total Expenses Display */}
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Typography variant="body2">Total Biaya</Typography>
          <Typography variant="body2">{formatMoney(totalExpenses)}</Typography>
        </Stack>

        {/* Tax Display */}
        {taxable && (
          <Stack direction="row" sx={{
            justifyContent: "space-between"
          }}>
            <Typography variant="body2">Pajak ({taxPercentage}%)</Typography>
            <Typography variant="body2">{formatMoney(taxAmount)}</Typography>
          </Stack>
        )}

        {/* Grand Total Display */}
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Typography sx={{
            fontWeight: "bold"
          }}>Grand Total</Typography>
          <Typography sx={{
            fontWeight: "bold"
          }}>{formatMoney(grandTotal)}</Typography>
        </Stack>

        {totalDownPayment > 0 && (
          <>
            <Stack direction="row" sx={{
              justifyContent: "space-between"
            }}>
              <Typography variant="body2">Uang Muka</Typography>
              <Typography variant="body2">
                -{formatMoney(totalDownPayment)}
              </Typography>
            </Stack>

            <Stack direction="row" sx={{
              justifyContent: "space-between"
            }}>
              <Typography sx={{
                fontWeight: "bold"
              }}>Terutang</Typography>
              <Typography sx={{
                fontWeight: "bold"
              }}>
                {formatMoney(outstanding)}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};
